import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SideBar from "./SideBar";
import UserProfileMenu from "./UserProfileMenu";
import { authFetch } from "../utils/auth";
import { DiagnosticMapWindow } from "./tse";
import CaseAnalysisPanels, { Section } from "./CaseAnalysisPanels";
import { useTenantConfig } from "../tenant/tenantConfig";
import { productTone } from "../tenant/productTone";

const FONT = "'Space Grotesk', sans-serif";

// ── Lightweight markdown → HTML for copilot bubbles ────────────────────────────
const formatBotMessage = (text) => {
  if (!text) return "";
  let t = String(text);
  t = t.replace(/^#{1,3}\s+/gm, "");
  t = t.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/\*(.*?)\*/g, "$1");
  t = t.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-700 underline">$1</a>',
  );
  t = t.replace(
    /(?<!["=])\b(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-cyan-700 underline">$1</a>',
  );
  t = t.replace(/^\s*[-•]\s+/gm, "• ");
  t = t.replace(/\n/g, "<br>");
  return t;
};

// ── Knowledge-graph → diagnostic map helpers (mirrors TSEDashboard2) ───────────
const buildSyntheticSnapshot = (fullGraph, payload = {}) => {
  if (!fullGraph && !payload.current_node_nid && !payload.traversal_path)
    return null;
  return {
    diagnostic_subgraph: fullGraph
      ? { nodes: fullGraph.nodes, edges: fullGraph.edges }
      : undefined,
    current_node_nid: payload.current_node_nid || null,
    traversal_path: payload.traversal_path || [],
    visited_nodes: payload.traversal_path || [],
    visited_node_details: payload.visited_node_details || {},
    eliminated_causes: payload.eliminated_causes || [],
  };
};

const ToolbarButton = ({ label, onClick, icon }) => (
  <button
    onClick={onClick}
    type="button"
    className="px-3 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition flex items-center gap-1.5"
  >
    {icon}
    {label}
  </button>
);

const NoteRow = ({ label, value }) => (
  <div className="mb-2.5">
    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
      {label}
    </div>
    <div className="text-sm text-[#253439] mt-0.5 whitespace-pre-wrap break-words">
      {value || "—"}
    </div>
  </div>
);

const CaseAnalysisNextSteps = ({ onLogout }) => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { products } = useTenantConfig();

  const [cases, setCases] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notesAppended, setNotesAppended] = useState("");
  const [toast, setToast] = useState("");
  const [caseListOpen, setCaseListOpen] = useState(false);

  // Copilot interactive thread (reuses /api/copilot/chat, seeded with case context)
  const [thread, setThread] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const sessionIdRef = useRef(null);
  const seededRef = useRef(false);
  const threadBodyRef = useRef(null);

  // Diagnostic map
  const fullGraphRef = useRef(null);
  const lastPayloadRef = useRef(null);
  const [diagMapOpen, setDiagMapOpen] = useState(false);
  const [diagMapMinimized, setDiagMapMinimized] = useState(false);
  const [diagMapSnapshot, setDiagMapSnapshot] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);

  // ── Load the case list (left rail) once ──────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await authFetch("/api/open-cases");
        if (res.ok) {
          const data = await res.json();
          if (active) setCases(Array.isArray(data.cases) ? data.cases : []);
        }
      } catch {
        /* non-fatal — the analysis call still carries the active case */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // ── Load analysis whenever the active case changes ───────────────────────────
  useEffect(() => {
    let active = true;
    // Reset per-case state
    setAnalysis(null);
    setCaseData(null);
    setError("");
    setLoading(true);
    setThread([]);
    setNotesAppended("");
    sessionIdRef.current = null;
    seededRef.current = false;
    fullGraphRef.current = null;
    lastPayloadRef.current = null;
    setDiagMapSnapshot(null);

    (async () => {
      try {
        const res = await authFetch("/api/case-analysis", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ case_id: caseId }),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || `Server error ${res.status}`);
        }
        const data = await res.json();
        if (!active) return;
        setAnalysis(data);
        setCaseData(data.case || null);
      } catch (err) {
        if (active) setError(err.message || "Failed to analyze case.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [caseId]);

  useEffect(() => {
    if (threadBodyRef.current)
      threadBodyRef.current.scrollTop = threadBodyRef.current.scrollHeight;
  }, [thread, isThinking]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  // ── Diagnostic map: fetch product knowledge graph on demand ──────────────────
  const fetchKnowledgeGraph = useCallback((product) => {
    if (!product || fullGraphRef.current) return;
    setGraphLoading(true);
    authFetch(`/api/knowledge-graph/${encodeURIComponent(product)}`)
      .then((res) =>
        res.ok ? res.json() : Promise.reject(new Error(`KG ${res.status}`)),
      )
      .then((data) => {
        const sub = data.diagnostic_subgraph || data;
        if ((sub.nodes || []).length) {
          fullGraphRef.current = { nodes: sub.nodes, edges: sub.edges || [] };
          setDiagMapSnapshot(
            buildSyntheticSnapshot(
              fullGraphRef.current,
              lastPayloadRef.current || {},
            ),
          );
        }
      })
      .catch(() => {})
      .finally(() => setGraphLoading(false));
  }, []);

  // ── Copilot thread (case-seeded) ─────────────────────────────────────────────
  const callCopilot = useCallback(
    async (userText, displayText) => {
      setThread((prev) => [
        ...prev,
        { sender: "agent", text: displayText || userText, ts: Date.now() },
      ]);
      setIsThinking(true);
      // Seed the first turn with the case context so the copilot is "mid-discussion".
      let content = userText;
      if (!seededRef.current && caseData) {
        const ctx =
          `Context — I am working an OPEN ${caseData.productType || ""} support case ` +
          `#${caseData.caseNumber || caseId}. Components: ${caseData.components || "n/a"}.\n` +
          `Customer description: ${caseData.caseDescription || caseData.reported_symptoms || ""}\n\n`;
        content = ctx + userText;
        seededRef.current = true;
      }
      try {
        const body = {
          messages: [{ role: "user", content }],
          mode: "copilot",
          ...(sessionIdRef.current ? { session_id: sessionIdRef.current } : {}),
        };
        const res = await authFetch("/api/copilot/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(
            (typeof e.detail === "string" && e.detail) ||
              `Server error ${res.status}`,
          );
        }
        const payload = await res.json();
        if (payload.session_id) sessionIdRef.current = payload.session_id;
        lastPayloadRef.current = payload;
        if (payload.selected_product)
          fetchKnowledgeGraph(payload.selected_product);

        const respText =
          (payload.info_content && payload.info_content.trim()) ||
          payload.response ||
          payload.content?.[0]?.text ||
          payload.message ||
          "(no response)";
        const options = (payload.next_steps || []).map((o) =>
          typeof o === "string"
            ? o
            : String(o.label ?? o.text ?? o.value ?? ""),
        );
        setThread((prev) => [
          ...prev,
          { sender: "copilot", text: respText, options, ts: Date.now() },
        ]);
      } catch (err) {
        setThread((prev) => [
          ...prev,
          { sender: "system", text: `Error: ${err.message}`, ts: Date.now() },
        ]);
      } finally {
        setIsThinking(false);
      }
    },
    [caseData, caseId, fetchKnowledgeGraph],
  );

  // ── Diagnostic map ───────────────────────────────────────────────────────────
  const openDiagMap = () => {
    const product = caseData?.productType;
    if (!fullGraphRef.current && product) fetchKnowledgeGraph(product);
    else
      setDiagMapSnapshot(
        buildSyntheticSnapshot(
          fullGraphRef.current,
          lastPayloadRef.current || {},
        ),
      );
    setDiagMapOpen(true);
    setDiagMapMinimized(false);
  };

  const openSearch = () => {
    window.open(
      `${window.location.origin}/TseAgentRag`,
      "_blank",
      "width=1200,height=800,scrollbars=yes,resizable=yes",
    );
  };

  // ── Add Analysis to Case Notes (local append for the demo) ───────────────────
  const addAnalysisToNotes = () => {
    if (!analysis) return;
    const lines = [];
    lines.push("── Arta Analysis ──");
    if (analysis.case_analysis)
      lines.push(`Analysis: ${analysis.case_analysis}`);
    if (analysis.root_cause_identified && analysis.root_cause) {
      lines.push(`Root Cause: ${analysis.root_cause}`);
    } else if ((analysis.potential_causes || []).length) {
      lines.push("Potential Causes:");
      analysis.potential_causes.forEach((pc, i) =>
        lines.push(`  ${i + 1}. (${pc.likelihood}) ${pc.cause}`),
      );
    }
    if ((analysis.clarifying_questions || []).length) {
      lines.push("Next-step questions:");
      analysis.clarifying_questions.forEach((q, i) =>
        lines.push(`  ${i + 1}. ${q}`),
      );
    }
    setNotesAppended(lines.join("\n"));
    showToast("Analysis added to Case Notes");
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  const c = caseData || {};

  return (
    <div
      className="flex w-full min-h-screen h-screen bg-[#FAFAF6] overflow-hidden"
      style={{ fontFamily: FONT }}
    >
      <SideBar
        mode="agent"
        onLogout={onLogout}
        isDarkMode={false}
        onToggleTheme={() => {}}
        onLinkClick={(p) => navigate(p)}
        currentPath="/open-cases"
      />

      <div className="flex-1 flex flex-col h-full relative overflow-hidden">
        <div className="absolute top-3 right-4 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>

        {/* Header */}
        <div className="px-6 pt-6 pb-3 border-b-2 border-[#06b6d4] flex-shrink-0 flex items-center gap-3">
          <button
            onClick={() => setCaseListOpen((v) => !v)}
            title={caseListOpen ? "Hide cases list" : "Show cases list"}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-[#06b6d4] hover:border-[#06b6d4] transition text-xs font-medium"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Cases
          </button>
          <h1 className="text-2xl font-bold text-[#253439]">
            Case Analysis and Next Steps
          </h1>
        </div>

        {/* 3-column body */}
        <div className="flex-1 flex overflow-hidden">
          {/* LEFT — case list (hidden by default) */}
          {caseListOpen && (
          <div className="w-[150px] flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
            <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-400 font-semibold border-b border-gray-100">
              {cases.length || ""} cases
            </div>
            {cases.map((item) => {
              const isActive = item.caseId === caseId;
              return (
                <button
                  key={item.caseId}
                  onClick={() =>
                    navigate(
                      `/case-analysis/${encodeURIComponent(item.caseId)}`,
                    )
                  }
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-100 transition ${
                    isActive
                      ? "bg-cyan-50 border-l-[3px] border-l-[#06b6d4]"
                      : "hover:bg-gray-50 border-l-[3px] border-l-transparent"
                  }`}
                >
                  <div
                    className={`text-[11px] font-bold ${productTone(item.productType, products).text}`}
                  >
                    {item.productType}
                  </div>
                  <div className="text-xs font-mono text-gray-600 truncate">
                    {item.caseNumber}
                  </div>
                </button>
              );
            })}
          </div>
          )}

          {/* MIDDLE — Case Notes */}
          <div className="w-[320px] flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto p-4">
            <h2 className="text-base font-bold text-[#253439] mb-3">
              Case Notes
            </h2>
            {loading && !caseData ? (
              <div className="text-sm text-gray-400">Loading…</div>
            ) : (
              <>
                <NoteRow label="Case #" value={c.caseNumber} />
                <NoteRow
                  label="Product"
                  value={`${c.product || ""}${c.productType ? ` (${c.productType})` : ""}`}
                />
                <NoteRow label="Case Description" value={c.caseDescription} />
                {notesAppended && (
                  <div className="mt-3 pt-3 border-t border-dashed border-cyan-300">
                    <div className="text-[11px] uppercase tracking-wide text-cyan-600 font-semibold mb-1">
                      Appended Analysis
                    </div>
                    <pre className="text-xs text-[#253439] whitespace-pre-wrap font-sans">
                      {notesAppended}
                    </pre>
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT — analysis + toolbar + copilot thread */}
          <div className="flex-1 flex flex-col overflow-hidden bg-[#FAFAF6]">
            {/* Toolbar */}
            <div className="px-5 py-3 border-b border-gray-200 bg-white flex-shrink-0 flex flex-wrap gap-2">
              <ToolbarButton
                label="Find Bugs"
                onClick={() => callCopilot("Find Bugs")}
              />
              <ToolbarButton
                label="Find Cases"
                onClick={() => callCopilot("Find Cases")}
              />
              <ToolbarButton
                label="Possible Causes"
                onClick={() => callCopilot("Possible Causes")}
              />
              <ToolbarButton label="Search" onClick={openSearch} />
              <ToolbarButton
                label="Diag Map"
                onClick={openDiagMap}
                icon={
                  <svg
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="12" cy="5" r="3" />
                    <circle cx="5" cy="19" r="3" />
                    <circle cx="19" cy="19" r="3" />
                    <path d="M12 8v3M7.5 17.5L10.5 11M16.5 17.5L13.5 11" />
                  </svg>
                }
              />
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {loading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <div className="flex items-center gap-1">
                    {[0, 150, 300].map((d) => (
                      <span
                        key={d}
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: `${d}ms` }}
                      />
                    ))}
                  </div>
                  Analyzing case…
                </div>
              ) : error ? (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              ) : analysis ? (
                <div className="max-w-3xl">
                <CaseAnalysisPanels
                  analysis={analysis}
                  showDiscussion={thread.length > 0}
                  discussion={
                    thread.length > 0 ? (
                      <Section title="Discussion">
                        <div
                          ref={threadBodyRef}
                          className="space-y-3 max-h-[360px] overflow-y-auto pr-1"
                        >
                          {thread.map((m, i) => (
                            <div
                              key={i}
                              className={`flex flex-col ${m.sender === "agent" ? "items-end" : "items-start"}`}
                            >
                              <div
                                className={`px-4 py-2.5 rounded-2xl border text-sm max-w-[90%] break-words ${
                                  m.sender === "agent"
                                    ? "bg-[#06b6d4] text-white border-[#06b6d4]"
                                    : m.sender === "system"
                                      ? "bg-red-50 text-red-700 border-red-200"
                                      : "bg-white text-[#253439] border-cyan-200"
                                }`}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: formatBotMessage(m.text),
                                  }}
                                />
                                {(m.options || []).length > 0 && (
                                  <div className="mt-2 flex flex-col gap-1.5">
                                    {m.options.map((opt, oi) => (
                                      <button
                                        key={oi}
                                        onClick={() => callCopilot(opt)}
                                        className="text-left px-3 py-1 border border-gray-200 rounded-lg text-xs text-[#253439] hover:border-[#06b6d4] hover:bg-cyan-50 transition"
                                      >
                                        {opt}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {isThinking && (
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <div className="flex items-center gap-1">
                                {[0, 150, 300].map((d) => (
                                  <span
                                    key={d}
                                    className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: `${d}ms` }}
                                  />
                                ))}
                              </div>
                              Copilot is thinking…
                            </div>
                          )}
                        </div>
                      </Section>
                    ) : null
                  }
                />
                </div>
              ) : null}
            </div>

            {/* Footer — Add Analysis to Case Notes */}
            <div className="flex-shrink-0 border-t border-gray-200 bg-white px-5 py-3">
              <div className="flex items-end justify-end gap-2">
                <button
                  onClick={addAnalysisToNotes}
                  disabled
                  className="bg-[#06b6d4] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition whitespace-nowrap"
                >
                  Add Analysis to Case Notes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-4 py-2 bg-[#253439] text-white text-sm rounded-lg shadow-lg">
          {toast}
        </div>
      )}

      <DiagnosticMapWindow
        open={diagMapOpen}
        minimized={diagMapMinimized}
        onOpen={() => setDiagMapMinimized(false)}
        onMinimize={() => setDiagMapMinimized(true)}
        onClose={() => {
          setDiagMapOpen(false);
          setDiagMapMinimized(false);
        }}
        trail={[]}
        snapshot={diagMapSnapshot}
        fullSnapshot={diagMapSnapshot}
        title="Diagnostic Map"
        isGraphLoading={graphLoading}
      />
    </div>
  );
};

export default CaseAnalysisNextSteps;
